import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset password · Surer Adda" },
      { name: "description", content: "Reset your Surer Adda account password." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPage,
});

function ResetPage() {
  const navigate = useNavigate();
  const [isRecovery, setIsRecovery] = useState(false);
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.includes("type=recovery")) setIsRecovery(true);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setIsRecovery(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const sendLink = async () => {
    if (!emailRegex.test(email.trim())) {
      setEmailError("Enter a valid email address");
      return;
    }
    setEmailError(null);
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success("Check your email for a reset link.");
  };

  const setNewPassword = async () => {
    if (pwd.length < 6) return setPwdError("Password must be at least 6 characters");
    if (pwd !== confirmPwd) return setPwdError("Passwords do not match");
    setPwdError(null);
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success("Password updated!");
    navigate({ to: "/", replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-2xl">{isRecovery ? "Set new password" : "Forgot password"}</h1>
          {isRecovery ? (
            <div className="mt-6 space-y-3">
              <Label className="text-xs">New password</Label>
              <Input type="password" autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
              <Label className="text-xs">Confirm new password</Label>
              <Input type="password" autoComplete="new-password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
              {pwdError && <p className="text-xs text-destructive">{pwdError}</p>}
              <Button disabled={loading} onClick={setNewPassword} className="w-full bg-primary text-primary-foreground">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <p className="text-sm text-muted-foreground">Enter the email you registered with. We'll send a secure link to reset your password.</p>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(null); }}
                placeholder="you@example.com"
              />
              {emailError && <p className="text-xs text-destructive">{emailError}</p>}
              <Button disabled={loading} onClick={sendLink} className="w-full bg-primary text-primary-foreground">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
